import { AllocationBarRow } from "web-core";
import { withRelativeBarPct } from "creso-core/utils";
import { ConditionalRenderer } from "@core/components/conditional-renderer";

type SectorRow = { name: string; aggregate: number };
type StockRow = { name: string; weightage: number };

export function exposureSectionVisible(
  topSectors: SectorRow[],
  topStocks: StockRow[]
) {
  return topSectors.length > 0 || topStocks.length > 0;
}

const LABELS = {
  sip: {
    sector: "Sector exposure - SIP",
    stocks: "Stocks exposure - SIP",
  },
  lumpsum: {
    sector: "Sector exposure - One time",
    stocks: "Stocks exposure - One time",
  },
} as const;

type ExposureSectionProps = {
  variant: "sip" | "lumpsum";
  topSectors: SectorRow[];
  topStocks: StockRow[];
  show?: "sectors" | "stocks";
};

export function ExposureSection({
  variant,
  topSectors,
  topStocks,
  show,
}: ExposureSectionProps) {
  const showSectors = !show || show === "sectors";
  const showStocks = !show || show === "stocks";
  const visible =
    (showSectors && topSectors.length > 0) ||
    (showStocks && topStocks.length > 0);
  const labels = LABELS[variant];

  if (!visible) return null;

  const sectorRows = withRelativeBarPct(
    topSectors
      .slice(0, 10)
      .map((sector) => ({ label: sector.name, pct: sector.aggregate }))
  );
  const stockRows = withRelativeBarPct(
    topStocks
      .slice(0, 10)
      .map((stock) => ({ label: stock.name, pct: stock.weightage }))
  );

  return (
    <div className="flex flex-row flex-1 gap-6">
      <ConditionalRenderer condition={showSectors && topSectors.length > 0}>
        <div className="flex-1 flex flex-col gap-2">
          <span className="text-lg font-semibold">{labels.sector}</span>
          <div className="flex flex-col gap-4 h-full border border-border rounded-lg p-4">
            {sectorRows.map((row) => (
              <AllocationBarRow
                key={row.label}
                label={row.label}
                pct={row.pct}
                barPct={row.barPct}
              />
            ))}
          </div>
        </div>
      </ConditionalRenderer>

      <ConditionalRenderer condition={showStocks && topStocks.length > 0}>
        <div className="flex-1 flex flex-col gap-2">
          <span className="text-lg font-semibold">{labels.stocks}</span>
          <div className="flex flex-col gap-4 h-full border border-border rounded-lg p-4">
            {stockRows.map((row) => (
              <AllocationBarRow
                key={row.label}
                label={row.label}
                pct={row.pct}
                barPct={row.barPct}
                enableAnimation={false}
              />
            ))}
          </div>
        </div>
      </ConditionalRenderer>
    </div>
  );
}
